import React from "react";
import { Link, useParams } from "react-router-dom";
import { articles, resources } from "@/content/resources";
import { Crumbs, ButtonPrimary, ButtonSecondary } from "@/components/NovaKit";

export default function ResourceArticle() {
  const { slug } = useParams();
  const article = articles[slug];
  if (!article) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24" data-testid="article-not-found">
        <Crumbs trail={[{ label: "Resources", to: "/resources" }, { label: "Not found" }]} />
        <h1 className="display text-4xl font-bold mt-6 mb-4">Article not found</h1>
        <ButtonSecondary to="/resources">Return to Resource Center</ButtonSecondary>
      </div>
    );
  }
  const paragraphs = article.body.split(/\n\n+/);
  return (
    <article data-testid="article-page" className="border-b hairline">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 pt-12 md:pt-16 pb-20">
        <Crumbs trail={[{ label: "Home", to: "/" }, { label: "Resources", to: "/resources" }, { label: article.kind }]} />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-10">
          <div className="lg:col-span-8">
            <div className="mono text-[11px] tracking-[0.25em] uppercase grad-text font-bold mb-4">{article.kind}</div>
            <h1 className="display text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.02] mb-4">{article.title}</h1>
            {article.subtitle && <p className="text-lg md:text-xl text-[#0A0E1A]/70 mb-10 max-w-3xl">{article.subtitle}</p>}
            <div className="h-px grad-bg mb-10 max-w-[100px]" />
            <div className="space-y-6 text-[16px] md:text-[17px] leading-[1.75] text-[#0A0E1A]/85 max-w-3xl">
              {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
              {article.bullets?.length > 0 && (
                <ul className="panel rounded-[20px] p-6 md:p-8 border hairline mt-6">
                  {article.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3 py-1.5 text-[#0A0E1A]/80">
                      <span className="grad-text font-bold mt-1">+</span><span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
              {article.sections?.length > 0 && (
                <div className="space-y-5 mt-8">
                  {article.sections.map((s, i) => (
                    <div key={s.title} className="card-hover bg-white rounded-[20px] border hairline p-6 md:p-8">
                      <div className="mono text-[10px] tracking-[0.22em] uppercase grad-text font-bold mb-3">Section · 0{i + 1}</div>
                      <h3 className="display text-xl md:text-2xl font-bold mb-3 leading-tight">{s.title}</h3>
                      <p className="text-sm md:text-base leading-relaxed text-[#0A0E1A]/75 mb-5">{s.body}</p>
                      {s.cta && (
                        <a href={s.cta.href} target="_blank" rel="noopener noreferrer"
                          className="grad-bg text-white rounded-full px-5 py-2.5 mono text-[11px] uppercase tracking-[0.18em] font-semibold inline-flex items-center gap-2 hover:opacity-90"
                          data-testid={`section-cta-${i}`}>
                          {s.cta.label} <span>↗</span>
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {article.footerNote && (
                <div className="panel rounded-[20px] border hairline p-6 md:p-8 mt-8">
                  <div className="mono text-[10px] tracking-[0.22em] uppercase grad-text font-bold mb-3">Footer note</div>
                  <p className="text-sm md:text-base text-[#0A0E1A]/75 leading-relaxed italic">{article.footerNote}</p>
                </div>
              )}
            </div>
            <div className="mt-14 flex flex-wrap gap-3">
              <ButtonPrimary to="/contact">Discuss with Hubinall</ButtonPrimary>
              <ButtonSecondary to="/resources">Back to Resources</ButtonSecondary>
            </div>
          </div>
          <aside className="lg:col-span-4 lg:pl-8 lg:border-l hairline">
            <div className="mono text-[10px] tracking-[0.25em] uppercase grad-text font-bold mb-5">More from the Resource Center</div>
            <ul className="space-y-4">
              {resources.groups.filter(g => g.slug !== slug).map(g => (
                <li key={g.slug}>
                  <Link to={`/resources/${g.slug}`} className="group flex items-start gap-3 text-sm text-[#0A0E1A] hover:text-[#4F46E5]">
                    <span className="grad-text font-bold mt-1">→</span>
                    <span className="display font-bold leading-snug">{g.title}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </aside>
        </div>
      </div>
    </article>
  );
}
